// Flotas tercerizadas del operador: líderes con su monto plano por entrega + los
// repartidores asociados a cada uno.
//
// La pantalla escribe en DOS servicios distintos, y por eso tiene DOS guardados:
//   · el MONTO del líder viaja en el PUT de la config de pago (versionada e inmutable),
//     junto con el resto del tarifario → botón "Guardar configuración" de la sección.
//   · la COMPOSICIÓN de la flota vive en microservice-users → botón propio por flota.
// Unificarlos en un solo botón obligaría a un commit en dos fases entre microservicios:
// si el segundo falla, el operador se queda con un monto sin flota (o al revés) y sin
// forma de saberlo. Separados, cada acción dice exactamente qué guardó.
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../../utils/toast";
import { Plus, Trash2, Users, Search, AlertTriangle, Save } from "lucide-react";
import { Button } from "../../../../components/ui/Button";
import { InfoHint } from "../../../../components/ui/InfoHint";
import { getDriverFleets, saveFleetMembers } from "../../../../api/driverFleets";

const inputCls =
  "h-9 rounded-md border border-border bg-bg px-2 text-sm w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500";

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Fecha local de hoy en formato YYYY-MM-DD (para el input date). */
const todayLocal = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/**
 * Medianoche LOCAL del día elegido, en ISO.
 *
 * No se manda la fecha suelta: el backend decide con ella a quién se le paga cada
 * entrega, y las entregas están marcadas con un instante UTC. Convertir acá —donde se
 * conoce la zona del operador— evita que "desde el 12" signifique las 21:00 del 11.
 */
const localMidnightIso = (ymd) => {
  const [y, m, d] = String(ymd || "").split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
};

// API (fleetLeaders[]) -> filas del formulario (montos como string).
export const fleetLeadersFromApi = (apiLeaders) =>
  (Array.isArray(apiLeaders) ? apiLeaders : []).map((f) => ({
    driverId: String(f?.driverId ?? ""),
    amount: f?.amountPerDelivery != null ? String(f.amountPerDelivery) : "",
  }));

// Filas -> API. Se descartan las incompletas (sin líder o sin monto > 0).
export const fleetLeadersToApi = (rows) =>
  (rows ?? [])
    .filter((r) => r.driverId && Math.round(num(r.amount)) > 0)
    .map((r) => ({
      driverId: r.driverId,
      amountPerDelivery: Math.round(num(r.amount)),
    }));

/** Cuántas filas se descartarían al guardar (aviso no bloqueante, como las zonas). */
export const countIncompleteFleetRows = (rows) =>
  (rows ?? []).filter((r) => !r.driverId || Math.round(num(r.amount)) <= 0).length;

export default function FleetLeadersEditor({
  rows,
  onChange,
  currency = "CLP",
  locale = "es-CL",
  // Ids de líder que YA están guardados en la versión activa de la config. Se usa para
  // avisar cuándo el monto todavía no aplica (la atribución exige las dos mitades).
  savedLeaderIds = [],
  // El simulador de la sección necesita los nombres para rotular su selector de flota, y
  // este componente ya trae el directorio: se los publica en vez de pedirlo dos veces.
  onDirectoryLoaded,
}) {
  const { t } = useTranslation();
  const [directory, setDirectory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const reload = () => {
    setLoading(true);
    return getDriverFleets()
      .then((res) => {
        const drivers = res?.data?.drivers ?? [];
        setDirectory(drivers);
        setLoadError(false);
        if (onDirectoryLoaded) {
          onDirectoryLoaded(
            Object.fromEntries(drivers.map((d) => [d.driverId, d.name || d.driverId])),
          );
        }
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // Se carga una vez: el catálogo de repartidores no cambia mientras se edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const byId = useMemo(
    () => new Map(directory.map((d) => [d.driverId, d])),
    [directory],
  );
  const savedSet = useMemo(
    () => new Set((savedLeaderIds ?? []).map(String)),
    [savedLeaderIds],
  );

  const fmt = (n) => {
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency || "CLP",
        maximumFractionDigits: 0,
      }).format(n || 0);
    } catch {
      return `${currency} ${n || 0}`;
    }
  };

  const addRow = () => onChange([...(rows ?? []), { driverId: "", amount: "" }]);
  const removeRow = (i) => onChange((rows ?? []).filter((_, idx) => idx !== i));
  const patchRow = (i, patch) =>
    onChange((rows ?? []).map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  // Un repartidor no puede liderar dos flotas ni figurar dos veces en la lista.
  const takenLeaderIds = new Set((rows ?? []).map((r) => r.driverId).filter(Boolean));

  return (
    <div>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <span className="text-sm font-medium text-fg flex items-center gap-1.5">
            {t("Flotas tercerizadas")}
            <InfoHint
              srLabel={t("Sobre las flotas tercerizadas")}
              label={t(
                "Para flota subcontratada: el repartidor que la lidera cobra un monto plano por cada entrega de su grupo y por las suyas. Los asociados dejan de ver montos en la app, porque su pago se lo gestiona él.",
              )}
            />
          </span>
          <p className="text-xs text-fg-muted mt-1">
            {t("Un líder cobra por su grupo y reparte internamente.")}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={addRow}
          iconLeft={<Plus className="w-4 h-4" />}
        >
          {t("Agregar flota")}
        </Button>
      </div>

      {loadError && (
        <p className="text-xs text-status-error mt-2">
          {t("No se pudo cargar el listado de repartidores. Puedes definir el monto igual, pero no asociar repartidores hasta recargar.")}
        </p>
      )}

      {(rows ?? []).length === 0 ? (
        <p className="text-xs text-fg-muted mt-2">
          {t("No tienes flotas tercerizadas configuradas.")}
        </p>
      ) : (
        <div className="flex flex-col gap-3 mt-3">
          {(rows ?? []).map((row, i) => (
            <FleetCard
              key={`${row.driverId || "new"}-${i}`}
              row={row}
              directory={directory}
              byId={byId}
              loadingDirectory={loading}
              takenLeaderIds={takenLeaderIds}
              isSaved={savedSet.has(row.driverId)}
              currencyFmt={fmt}
              onPatch={(patch) => patchRow(i, patch)}
              onRemove={() => removeRow(i)}
              onMembersSaved={reload}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FleetCard({
  row,
  directory,
  byId,
  loadingDirectory,
  takenLeaderIds,
  isSaved,
  currencyFmt,
  onPatch,
  onRemove,
  onMembersSaved,
}) {
  const { t } = useTranslation();

  // Composición actual (la del backend) y la que el operador está editando.
  const currentMemberIds = useMemo(
    () =>
      directory
        .filter((d) => d.leaderId === row.driverId)
        .map((d) => d.driverId),
    [directory, row.driverId],
  );
  const [selected, setSelected] = useState(() => new Set(currentMemberIds));
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState(todayLocal);
  const [saving, setSaving] = useState(false);

  // Al recargar el directorio (o cambiar de líder) la selección vuelve a lo persistido.
  useEffect(() => {
    setSelected(new Set(currentMemberIds));
  }, [currentMemberIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = useMemo(() => {
    if (selected.size !== currentMemberIds.length) return true;
    return currentMemberIds.some((id) => !selected.has(id));
  }, [selected, currentMemberIds]);

  // Candidatos: repartidores del operador que no son este líder. Los que ya lideran o
  // pertenecen a OTRA flota se muestran deshabilitados con el motivo, en vez de
  // ocultarse: si no aparece, el operador cree que el listado está incompleto.
  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return directory
      .filter((d) => d.driverId !== row.driverId)
      .filter((d) => !q || (d.name ?? "").toLowerCase().includes(q) || (d.email ?? "").toLowerCase().includes(q))
      .map((d) => {
        const inOtherFleet = d.leaderId && d.leaderId !== row.driverId;
        const leadsOwnFleet = d.memberCount > 0 || takenLeaderIds.has(d.driverId);
        return {
          ...d,
          disabledReason: inOtherFleet
            ? t("En la flota de {{name}}", {
                name: byId.get(d.leaderId)?.name ?? t("otro líder"),
              })
            : leadsOwnFleet
              ? t("Lidera su propia flota")
              : null,
        };
      });
  }, [directory, row.driverId, search, takenLeaderIds, byId, t]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSaveMembers = async () => {
    setSaving(true);
    try {
      await saveFleetMembers(row.driverId, {
        memberIds: [...selected],
        from: localMidnightIso(from),
      });
      toast.success(t("Flota actualizada."));
      await onMembersSaved();
    } catch (err) {
      toast.error(
        err.response?.data?.message ?? t("No se pudo actualizar la flota."),
      );
    } finally {
      setSaving(false);
    }
  };

  const amountNum = Math.round(num(row.amount));

  return (
    <div className="rounded-lg border border-border bg-bg-muted p-3">
      {/* Líder + monto: van al PUT de la config (versionado). */}
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <label className="text-xs text-fg-muted">{t("Líder de la flota")}</label>
          <select
            className={inputCls}
            value={row.driverId}
            onChange={(e) => onPatch({ driverId: e.target.value })}
            disabled={loadingDirectory}
          >
            <option value="">
              {loadingDirectory ? t("Cargando…") : t("Selecciona un repartidor")}
            </option>
            {directory.map((d) => (
              <option
                key={d.driverId}
                value={d.driverId}
                // Un repartidor que pertenece a otra flota no puede liderar (sin
                // anidamiento); el backend lo rechaza igual, esto solo lo adelanta.
                disabled={
                  !!d.leaderId ||
                  (takenLeaderIds.has(d.driverId) && d.driverId !== row.driverId)
                }
              >
                {d.name || d.driverId}
                {d.leaderId ? ` · ${t("ya está en una flota")}` : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="w-[180px]">
          <div className="flex items-center gap-1">
            <label className="text-xs text-fg-muted">{t("Monto por entrega")}</label>
            <InfoHint
              srLabel={t("Sobre el monto de la flota")}
              label={t("Monto plano: reemplaza el tarifario completo. No aplica ajuste por comuna, ni por código postal, ni por tipo de vehículo, ni recargo por sobredimensionado.")}
            />
          </div>
          <input
            type="number"
            min="1"
            className={inputCls}
            value={row.amount}
            onChange={(e) => onPatch({ amount: e.target.value })}
            placeholder={t("Ej: 1200")}
          />
        </div>
        <Button
          variant="ghost"
          onClick={onRemove}
          iconLeft={<Trash2 className="w-4 h-4" />}
          aria-label={t("Quitar flota")}
        >
          {t("Quitar")}
        </Button>
      </div>

      {amountNum > 0 && (
        <p className="text-xs text-fg-muted mt-2">
          {t("Cada entrega de la flota paga")} {currencyFmt(amountNum)}.
        </p>
      )}

      {row.driverId && !isSaved && (
        <p className="text-xs text-status-warning mt-1 flex items-start gap-1">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          {t("Sin guardar: hasta que guardes, a los asociados se les paga su tarifa normal.")}
        </p>
      )}

      {/* Composición de la flota: guardado propio contra microservice-users. */}
      {row.driverId && (
        <div className="mt-3 border-t border-border/60 pt-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs font-medium text-fg inline-flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              {t("Repartidores de la flota")} ({selected.size})
            </span>
            <div className="flex items-end gap-2">
              <div>
                <div className="flex items-center gap-1">
                  <label className="text-xs text-fg-muted">{t("Vigente desde")}</label>
                  <InfoHint
                    align="end"
                    srLabel={t("Sobre la vigencia")}
                    label={t("Los días anteriores a esta fecha no cambian de dueño: lo ya entregado se le sigue pagando a quien correspondía.")}
                  />
                </div>
                <input
                  type="date"
                  className={`${inputCls} w-[150px]`}
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </div>
              <Button
                variant="primary"
                onClick={handleSaveMembers}
                disabled={saving || !dirty}
                iconLeft={<Save className="w-4 h-4" />}
              >
                {saving ? t("Guardando…") : t("Guardar flota")}
              </Button>
            </div>
          </div>

          <div className="relative mt-2">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-fg-subtle" />
            <input
              className={`${inputCls} pl-7`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("Buscar repartidor…")}
            />
          </div>

          <div className="max-h-56 overflow-auto mt-2 rounded-md border border-border bg-bg">
            {candidates.length === 0 ? (
              <p className="text-xs text-fg-muted p-2">
                {loadingDirectory ? t("Cargando…") : t("Sin repartidores para mostrar.")}
              </p>
            ) : (
              candidates.map((d) => {
                const checked = selected.has(d.driverId);
                // Un miembro ya seleccionado nunca se deshabilita: hay que poder sacarlo.
                const disabled = !checked && !!d.disabledReason;
                return (
                  <label
                    key={d.driverId}
                    className={`flex items-center gap-2 px-2 py-1.5 border-b border-border/50 last:border-0 text-sm ${
                      disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-bg-subtle"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggle(d.driverId)}
                    />
                    <span className="text-fg truncate">{d.name || d.driverId}</span>
                    {!d.active && (
                      <span className="text-xs text-fg-subtle">· {t("inactivo")}</span>
                    )}
                    {disabled && (
                      <span className="text-xs text-fg-subtle ml-auto shrink-0">
                        {d.disabledReason}
                      </span>
                    )}
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
